<script>
  import * as yup from 'yup';
  import { createEventDispatcher } from 'svelte';
  import truncate from 'lodash/truncate';

  import { Button, FormGroup, InlineNotification, Modal, Tag, TextArea, TextInput } from 'carbon-components-svelte';
  import { Form } from '@mst-fe/sveltejs-forms';

  import AddIcon from 'carbon-icons-svelte/lib/Add16';

  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import { addDataFeed } from '../../services';
  import { getServerErrorMessage } from '../../utils';

  const INITIAL_PATH_STATE = {
    path: '',
    issue: '',
  };
  const MESSAGES = {
    warningSelections: 'Typed destination is already under selections',
  };

  const initialValues = { paths: [] };

  export let open = false;

  let loading = false,
    formSubmitButtonRef,
    resultNotification = null,
    pathState = { ...INITIAL_PATH_STATE };

  const dispatch = createEventDispatcher();

  async function handleFormSubmission({ detail: { values } }) {
    resultNotification = null;
    loading = true;

    try {
      const feed = await addDataFeed(values);
      dispatch('close', feed);
    } catch (error) {
      const errorMessage = getServerErrorMessage(error) ?? 'Please try again later.';
      console.error('[AddDataFeedModal] Failed to add feed!', errorMessage);

      resultNotification = {
        hideCloseButton: false,
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to add feed! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  function handleCustomChangeEvent({ key, setValue }) {
    return function onCustomChange({ detail: value }) {
      setValue(key, value);
    };
  }

  function handleChangeEvent({ key, setValue }) {
    return function onChange({ target: { value } }) {
      setValue(key, value);
    };
  }

  function handlePathState(key) {
    return function onPathStateChange({ detail: value }) {
      pathState = {
        ...pathState,
        [key]: value,
      };
    };
  }

  function handlePathAddition({ key, setValue, values }) {
    if (values.includes(pathState.path)) {
      pathState = {
        ...pathState,
        issue: 'warning',
      };
      return;
    }

    const newValues = [...values, pathState.path];
    setValue(key, newValues);
    pathState = { ...INITIAL_PATH_STATE };
  }

  function handlePathRemoval({ key, setValue, valueToRemove, values }) {
    const newValues = values.filter((value) => value !== valueToRemove);
    setValue(key, newValues);
  }

  function onModalSubmit() {
    if (!formSubmitButtonRef) {
      throw new Error('Form submit button not found!');
    }

    formSubmitButtonRef.click();
  }

  function onModalClose() {
    dispatch('close');
  }

  const schema = yup.object().shape({
    name: yup.string().required('Feed name cannot be empty').matches(/^\S*$/, 'Feed name cannot contain spaces'),
    paths: yup
      .array()
      .of(
        yup
          .string()
          .test('no-space', 'No space is allowed', (value) => /^\S*$/.test(value))
          .test('no-leading-slash', 'No leading slash is allowed', (value) => !value?.startsWith('/'))
          .test('no-trailing-slash', 'No trailing slash is allowed', (value) => !value?.endsWith('/'))
          .test('must-have-two-slashes', 'Must contain exactly two slashes (e.g., bucket/nic/feed)', (value) => {
            return (value?.match(/\//g) || []).length === 2;
          })
          .test('no-double-slash', 'No double slash is allowed', (value) => !value?.includes('//'))
          .test('invalid-bucket-name', 'Invalid bucket name; should start with "p-lseg-ll-" excluding "p-lseg-ll-feeds"', (value) => {
            /* eslint-disable-next-line no-unsafe-optional-chaining */
            const [bucketName] = value?.split('/');
            return /^(p-lseg-ll-|d-lseg-ll-)[a-z0-9.-]+$/.test(bucketName) && bucketName !== 'p-lseg-ll-feeds';
          })
      )
      .min(1, 'Raw Feed paths cannot be empty. Please provide at least one valid path: <bucket_name>/<path_to_feed>'),
    notes: yup.string().nullable(),
  });
</script>

<Modal
  hasForm
  modalHeading="Add Feed"
  primaryButtonText="Add"
  secondaryButtonText="Cancel"
  primaryButtonDisabled={loading}
  preventCloseOnClickOutside
  shouldSubmitOnEnter={false}
  bind:open
  on:click:button--secondary={onModalClose}
  on:close={onModalClose}
  on:submit={onModalSubmit}
>
  <LoadingSpinner {loading}>
    {#if resultNotification}
      <InlineNotification
        kind={resultNotification.kind}
        lowContrast
        title={resultNotification.title}
        subtitle={resultNotification.subtitle}
      />
    {/if}
    <InlineNotification
      lowContrast
      hideCloseButton
      kind="info"
      subtitle="Manual adding a feed is currently only supported for AWS Raw Feeds"
    />
    <Form
      fieldDomString="input,textarea,div[name]"
      validateOnBlur={false}
      validateOnChange={false}
      {initialValues}
      {schema}
      let:submitForm
      let:errors
      let:setValue
      let:touched
      let:values
      on:submit={handleFormSubmission}
    >
      <FormGroup>
        <TextInput
          name="name"
          autocomplete="off"
          invalid={touched.name && !!errors.name}
          invalidText={errors.name}
          labelText="Name"
          placeholder="Feed name"
          value={values.name}
          required
          on:change={handleCustomChangeEvent({ key: 'name', setValue })}
        />
        <TextArea
          invalid={touched.notes && !!errors.notes}
          invalidText={errors.notes}
          labelText="Notes"
          name="notes"
          placeholder="Additional notes"
          type="text"
          value={values.notes}
          on:change={handleChangeEvent({ key: 'notes', setValue })}
        />
      </FormGroup>
      <FormGroup legendText="AWS Path to add">
        <div class="upload-controls">
          <div class="path-input">
            <TextInput
              autocomplete="off"
              value={pathState.path}
              on:change={handlePathState('path')}
              placeholder="e.g. <bucket_name>/<nic>/<feed>"
            />
          </div>
          <Button
            disabled={!pathState.path}
            size="field"
            iconDescription="Add path"
            icon={AddIcon}
            on:click={handlePathAddition({ key: 'paths', setValue, values: values.paths })}
          />
        </div>
        {#if pathState.issue}
          <span class="{pathState.issue} bx--label">{MESSAGES[`${pathState.issue}Selections`]}</span>
        {/if}
        <div class="selections-wrapper">
          <span class="bx--label bx--label--required">Selected path(s)</span>
          <div
            class={touched.paths && !!errors.paths ? 'error selections' : 'selections'}
            name="paths"
            aria-label="Created paths"
            role="listbox"
            tabindex="0"
          >
            {#if !values.paths?.length}
              <span>Use input above to add paths. Your selections will appear here.</span>
            {:else}
              {#each values.paths as value}
                <div class="destination-tag">
                  <Tag
                    filter
                    on:close={handlePathRemoval({
                      key: 'paths',
                      setValue,
                      valueToRemove: value,
                      values: values.paths,
                    })}
                    on:click={(e) => e.preventDefault()}
                  >
                    <strong class="path-label" {...value.length > 65 ? { 'data-path': value } : {}}>
                      {truncate(value, { length: 65 })}
                    </strong>
                  </Tag>
                </div>
              {/each}
            {/if}
          </div>
          {#if touched.paths && !!errors.paths}
            <span class="error bx--label">{errors.paths}</span>
          {/if}
        </div>
      </FormGroup>
      <button hidden type="button" on:click={submitForm} bind:this={formSubmitButtonRef} />
    </Form>
  </LoadingSpinner>
</Modal>

<style>
  .upload-controls {
    display: flex;
    align-items: flex-end;
  }

  .upload-controls .path-input {
    margin: 0 0.25rem 0 0;
    flex: 2;
  }

  .selections-wrapper {
    margin-top: 0.75rem;
  }

  .selections {
    background-color: var(--cds-field-02);
    padding: 0.5rem;
    min-height: 100px;
    outline-offset: -2px;
  }

  .selections:focus {
    outline: 2px solid var(--cds-focus) !important;
  }

  .selections > span {
    color: var(--cds-text-helper);
    font-style: italic;
    font-size: 12px;
  }

  .selections :global(.bx--tag) {
    border: 1px solid var(--cds-ui-04);
  }

  .warning.bx--label {
    color: var(--outline-warning-color);
  }

  .error.bx--label {
    color: var(--cds-support-01);
  }

  .error.selections {
    outline: 2px solid var(--cds-support-01);
  }

  .destination-tag :global(.bx--tag) {
    position: relative;
  }

  .path-label[data-path]:hover::after {
    content: attr(data-path);
    position: absolute;
    top: 1.25rem;
    left: -1rem;
    z-index: 1;
    background-color: var(--cds-tag-background-gray, #e0e0e0);
    border-radius: 0.9365rem;
    padding: 0.1rem 0.5rem;
    white-space: nowrap;
    cursor: default;
    border: 1px solid var(--cds-ui-04);
  }
</style>
